import {
  Component,
  Suspense,
  lazy,
  useCallback,
  useMemo,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react';
import { WindowSkeleton } from '@/shared/ui/atoms';
import { DataLoadError } from '@/shared/ui/DataLoadError';

interface WindowModule<P> {
  default: ComponentType<P>;
}

type WindowModuleLoader<P> = () => Promise<WindowModule<P>>;

const RECOVERY_DESCRIPTION =
  '새 버전이 배포되면 이전 화면 파일이 사라져 열리지 않습니다. 다시 시도해도 열리지 않으면 새로고침해 주세요.';

/**
 * 창 모듈 로딩 실패를 알린다.
 *
 * 배너와 같은 채널(`vite:preloadError`)로 알려 새로고침 버튼을 가진 DeploymentBanner 를 띄운다 —
 * 배너의 기존 동작은 그대로 두고, 창 안에는 재시도를 배너에는 새로고침을 남기는 분담이다.
 */
function announceLoadFailure(subject: string): Error {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('vite:preloadError'));
  }
  return new Error(`${subject} 창 모듈을 불러오지 못했습니다`);
}

async function loadWindowModule<P>(
  subject: string,
  load: WindowModuleLoader<P>
): Promise<WindowModule<P>> {
  let mod: WindowModule<P> | undefined;
  try {
    mod = await load();
  } catch {
    throw announceLoadFailure(subject);
  }

  // 누군가 `vite:preloadError` 를 preventDefault() 하면 Vite preload 헬퍼는
  // 던지지 않고 undefined 로 resolve 한다. 그 빈 값이 여기서 걸리지 않으면 React 가
  // "Element type is invalid" 로 트리를 버려 창이 백지가 된다.
  if (!mod || mod.default == null) {
    throw announceLoadFailure(subject);
  }
  return mod;
}

interface BoundaryProps {
  subject: string;
  onRetry: () => void;
  children: ReactNode;
}

class WindowLoadBoundary extends Component<BoundaryProps, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  private handleRetry = () => {
    this.setState({ failed: false });
    this.props.onRetry();
  };

  override render() {
    if (this.state.failed) {
      return (
        <DataLoadError
          subject={`${this.props.subject} 창`}
          description={RECOVERY_DESCRIPTION}
          onRetry={this.handleRetry}
        />
      );
    }
    return this.props.children;
  }
}

/**
 * 창 등록부 전용 지연 로딩 — 실패해도 창 하나만 복구 화면으로 바뀐다.
 *
 * 맨 `lazy()` 는 실패를 조용히 빈 화면으로 흘리고(위 loadWindowModule 주석), 창 전체를 감싸는
 * 공용 경계는 창 평면을 통째로 대체하는 데다 React.lazy 가 해소값을 캐시하는 탓에 재시도가
 * 같은 실패를 반복한다. 시도마다 새 lazy 를 만들어 캐시를 벗어나고, 경계를 창 단위로 내린다.
 */
export function lazyWindow<P extends object>(
  subject: string,
  load: WindowModuleLoader<P>
): ComponentType<P> {
  function LazyWindow(props: P) {
    const [attempt, setAttempt] = useState(0);
    const Loaded = useMemo(
      () => lazy(() => loadWindowModule(subject, load)),
      // eslint-disable-next-line react-hooks/exhaustive-deps -- 시도마다 새 lazy 를 만들어야 재시도가 캐시를 벗어난다
      [attempt]
    );
    const retry = useCallback(() => setAttempt((n) => n + 1), []);

    return (
      <WindowLoadBoundary key={attempt} subject={subject} onRetry={retry}>
        <Suspense fallback={<WindowSkeleton />}>
          <Loaded {...props} />
        </Suspense>
      </WindowLoadBoundary>
    );
  }
  LazyWindow.displayName = `LazyWindow(${subject})`;
  return LazyWindow;
}
